import React from 'react';
import { ErrorMessage } from '../types/websocket';
import './ErrorDisplay.css';

export interface ErrorDisplayProps {
  /** Error message to display */
  error: ErrorMessage | string | null;
  /** Whether to show the error (for animation control) */
  show?: boolean;
  /** Callback when user dismisses the error */
  onDismiss?: () => void;
  /** Callback when user clicks retry */
  onRetry?: () => void;
  /** Additional CSS class name */
  className?: string;
}

/**
 * ErrorDisplay component for showing user-friendly error messages with recovery instructions
 * Requirements: 9.1, 9.2, 9.3
 */
export const ErrorDisplay: React.FC<ErrorDisplayProps> = ({
  error,
  show = true,
  onDismiss,
  onRetry,
  className = '',
}) => {
  if (!error || !show) {
    return null;
  }

  // Handle both ErrorMessage objects and simple strings
  const errorInfo = typeof error === 'string' 
    ? { code: 'GENERIC_ERROR', message: error, recoverable: true, timestamp: Date.now() }
    : error;

  // Determine error severity and styling based on error code
  const getErrorSeverity = (code: string): 'low' | 'medium' | 'high' | 'critical' => {
    if (code.includes('CREDENTIALS') || code.includes('AUTH')) return 'critical';
    if (code.includes('NETWORK') || code.includes('CONNECTION')) return 'high';
    if (code.includes('LIMIT') || code.includes('OVERLOAD')) return 'medium';
    return 'low';
  };

  // Get recovery instructions based on error code
  const getRecoveryInstructions = (code: string): string[] => {
    switch (code) {
      case 'AWS_CREDENTIALS_NOT_FOUND':
      case 'AWS_INVALID_CREDENTIALS':
      case 'AWS_ACCESS_DENIED':
        return [
          'Check that AWS credentials are properly configured',
          'Verify your AWS access keys and permissions',
          'Contact your administrator if the issue persists'
        ];
      
      case 'AWS_SERVICE_LIMIT_EXCEEDED':
        return [
          'Wait a few minutes and try again',
          'Try again during off-peak hours',
          'Contact support if limits are consistently exceeded'
        ];
      
      case 'CONNECTION_ERROR':
      case 'NETWORK_ERROR':
        return [
          'Check your internet connection',
          'Try refreshing the page',
          'Verify the server is running and accessible'
        ];
      
      case 'INVALID_AUDIO_FORMAT':
      case 'AUDIO_PROCESSING_ERROR':
        return [
          'Check your microphone settings',
          'Try restarting the browser',
          'Ensure your microphone is working properly'
        ];
      
      case 'MICROPHONE_PERMISSION_DENIED':
        return [
          'Click the microphone icon in your browser\'s address bar',
          'Select "Allow" for microphone access',
          'Refresh the page after granting permission'
        ];
      
      case 'BROWSER_NOT_SUPPORTED':
        return [
          'Use a modern browser like Chrome, Firefox, or Safari',
          'Update your browser to the latest version',
          'Enable JavaScript if it\'s disabled'
        ];
      
      default:
        if (errorInfo.recoverable) {
          return [
            'Try the action again',
            'Refresh the page if the problem continues',
            'Contact support if the issue persists'
          ];
        } else {
          return [
            'This error requires manual intervention',
            'Contact your administrator or support team',
            'Provide the error code when reporting the issue'
          ];
        }
    }
  };

  const severity = getErrorSeverity(errorInfo.code);
  const recoveryInstructions = getRecoveryInstructions(errorInfo.code);

  // Get user-friendly title based on error code
  const getErrorTitle = (code: string): string => {
    if (code.includes('CREDENTIALS') || code.includes('AUTH')) return 'Authentication Error';
    if (code.includes('NETWORK') || code.includes('CONNECTION')) return 'Connection Error';
    if (code.includes('LIMIT') || code.includes('OVERLOAD')) return 'Service Limit Exceeded';
    if (code.includes('AUDIO') || code.includes('MICROPHONE')) return 'Audio Error';
    if (code.includes('BROWSER') || code.includes('COMPATIBILITY')) return 'Browser Compatibility';
    return 'Error';
  };

  const title = getErrorTitle(errorInfo.code);

  return (
    <div className={`error-display error-display--${severity} ${className}`} role="alert">
      <div className="error-display__container">
        <div className="error-display__header">
          <div className="error-display__icon">
            {severity === 'critical' && '🚨'}
            {severity === 'high' && '⚠️'}
            {severity === 'medium' && '⚡'}
            {severity === 'low' && 'ℹ️'}
          </div>
          <div className="error-display__title-section">
            <h3 className="error-display__title">{title}</h3>
            <p className="error-display__message">{errorInfo.message}</p>
          </div>
          {onDismiss && (
            <button
              className="error-display__dismiss"
              onClick={onDismiss}
              aria-label="Dismiss error"
              type="button"
            >
              ✕
            </button>
          )}
        </div>

        {recoveryInstructions.length > 0 && (
          <div className="error-display__recovery">
            <h4 className="error-display__recovery-title">How to fix this:</h4>
            <ul className="error-display__recovery-list">
              {recoveryInstructions.map((instruction, index) => (
                <li key={index} className="error-display__recovery-item">
                  {instruction}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="error-display__actions">
          {errorInfo.recoverable && onRetry && (
            <button
              className="error-display__retry-button"
              onClick={onRetry}
              type="button"
            >
              Try Again
            </button>
          )}
          
          <div className="error-display__details">
            <span className="error-display__code">Error Code: {errorInfo.code}</span>
            <span className="error-display__timestamp">
              {new Date(errorInfo.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorDisplay;